import { useLayoutEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  isWorkflowVersionTracesSearchContextValid,
  migrateWorkflowVersionTracesSearchContextParams,
  parseWorkflowVersionTracesSearchContext,
  resetWorkflowVersionTracesSearchContextParams,
} from "../utils/workflowVersionTracesSearchContext";

/** Validates URL search context for the current workflow version; migrates or resets as needed. */
export const useEnsureWorkflowVersionTracesSearchContext = (
  workflowId: string | undefined,
  versionNumber: string | undefined,
): boolean => {
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useMemo(() => {
    if (!workflowId || !versionNumber) {
      return null;
    }
    const parsed = {
      workflowId: Number(workflowId),
      workflowVersionNumber: Number(versionNumber),
    };
    if (!Number.isFinite(parsed.workflowId) || !Number.isFinite(parsed.workflowVersionNumber)) {
      return null;
    }
    return parsed;
  }, [versionNumber, workflowId]);

  const isReady = !context || isWorkflowVersionTracesSearchContextValid(searchParams, context);

  useLayoutEffect(() => {
    if (!context || isReady) {
      return;
    }

    const stored = parseWorkflowVersionTracesSearchContext(searchParams);
    const nextParams = stored
      ? resetWorkflowVersionTracesSearchContextParams(searchParams, context)
      : migrateWorkflowVersionTracesSearchContextParams(searchParams, context);

    setSearchParams(nextParams, { replace: true });
  }, [context, isReady, searchParams, setSearchParams]);

  return isReady;
};
